import { extractRouteParams } from '@/domains/routing';
import { useSimulationRouting } from '@/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalStore } from '@/store';
import { useStoreInitialization } from '@/store/hooks/useStoreInitialization';
import { useParams } from 'next/navigation';

/**
 * Centralized feature hook for simulation scenario preparation behavior.
 *
 * Keeps routing and loading business logic out of the view component.
 *
 * Responsibilities:
 * - Read active certification/simulation context from the global store.
 * - Derive a stable loading state for the prep view.
 * - Guard against invalid prep states by redirecting to dashboard.
 * - Expose a start handler that routes to role-play when IDs are available.
 *
 * Notes:
 * - `isLoading` accounts for both initial bootstrapping AND the window between
 *   data loading completing and `useActiveCertificationFromRoute` (in the template)
 *   restoring active state from URL params. Without this, a full-page reload on
 *   mobile can cause the guard to fire before state is restored, redirecting to
 *   the dashboard instead of staying on the prep page.
 */
export const useSimulationScenarioPrepFeature = () => {
  const ROUTE_RESTORE_GRACE_MS = 3000;

  // ================================
  // Routing dependencies
  // ================================
  // Navigation callbacks are centralized in `useSimulationRouting`.
  // We only consume the two actions required by this prep flow.
  const { navigateToDashboard, navigateToRoleplay } = useSimulationRouting();

  // ================================
  // URL param extraction
  // ================================
  // Read certificationId and simulationId from the dynamic route segments.
  // These are used to detect whether state can be restored from the URL
  // on a full-page reload (e.g. on mobile), before the template-level
  // `useActiveCertificationFromRoute` hook has a chance to run.
  const { certificationId, simulationId } = extractRouteParams(useParams());

  // ================================
  // Store dependencies
  // ================================
  // Initialization loading reflects app-level bootstrapping.
  const { isLoading: isLoadingInit } = useStoreInitialization();

  // Feature data from global store:
  // - `activeCertification`: current selected certification context.
  // - `activeSimulation`: current selected simulation to prepare.
  // - `certifications`/`simulations`: raw collections used for URL-based lookups.
  // - `isLoadingStore`: store-level loading for fetched entities.
  // - `personas`: persona catalog passed through to the view component.
  const {
    activeCertification,
    activePersona,
    activeSimulation,
    certifications = [],
    isLoading: isLoadingStore,
    personas = [],
    setActiveCertification,
    setActivePersona,
    setActiveSimulation,
    simulations = [],
  } = useGlobalStore();

  // ===============================
  // Route-based state restoration warmup
  // ================================

  // Constants to determine when to hold the prep guard for route-based state restoration.
  const hasRouteParams = Boolean(certificationId && simulationId);
  const hasActiveContext = Boolean(activeCertification && activeSimulation);
  const catalogsAreReady = certifications.length > 0 && simulations.length > 0;
  const shouldHoldForCatalogWarmup = hasRouteParams && !hasActiveContext && !catalogsAreReady;
  const [isRouteWarmupExpired, setIsRouteWarmupExpired] = useState(false);

  // Hold the prep guard on initial load when URL params are present but active state
  // hasn't been restored yet. This prevents a flash of the guard firing and redirecting
  // to the dashboard on slow mobile reloads, where the store may have data but
  // `useActiveCertificationFromRoute` (which runs in the template ancestor) hasn't
  // had a chance to restore active state from the URL. We also set a hard timeout to
  // avoid holding the guard indefinitely in edge cases.
  useEffect(() => {
    if (!shouldHoldForCatalogWarmup) {
      setIsRouteWarmupExpired(false);
      return;
    }

    const timerId = window.setTimeout(() => {
      setIsRouteWarmupExpired(true);
    }, ROUTE_RESTORE_GRACE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [shouldHoldForCatalogWarmup]);

  // ================================
  // Derived state
  // ================================

  /**
   * Whether the current URL points to a cert+sim pair that exists in the
   * loaded store data. When `isLoadingStore` is still true we optimistically
   * treat the route as valid to avoid a premature redirect while data is in
   * flight.
   *
   * Used to hold the guard while `useActiveCertificationFromRoute` (which runs
   * in the template ancestor and therefore fires *after* this hook's effects in
   * React's bottom-up effect execution order) restores active state.
   */
  const isValidRoute = useMemo(() => {
    if (!certificationId || !simulationId) return false;
    if (isLoadingStore) return true; // Data still in flight – optimistically valid

    // Check if the cert+sim from the URL actually exist in the loaded store data.
    const certExists = certifications.some((c) => c.id.toString() === certificationId);
    const simExists = simulations.some((s) => s.id.toString() === simulationId);

    // Only consider the route valid if both cert and sim exist.
    return certExists && simExists;
  }, [certificationId, simulationId, certifications, simulations, isLoadingStore]);

  /**
   * Resolve certification/simulation objects from the route whenever the URL points
   * at a valid prep page. This lets the prep view self-heal on full-page reloads
   * even before ancestor hooks restore active state.
   */
  const routeCertification = useMemo(() => {
    if (!certificationId) return null;
    return (
      certifications.find((certification) => certification.id.toString() === certificationId) ??
      null
    );
  }, [certificationId, certifications]);

  /**
   * Similarly, resolve the route simulation from the URL for convenience. This is used
   * to derive the route persona as well, and to restore active state on reloads before
   * ancestor hooks run.
   */
  const routeSimulation = useMemo(() => {
    if (!simulationId) return null;
    return simulations.find((simulation) => simulation.id.toString() === simulationId) ?? null;
  }, [simulationId, simulations]);

  /**
   * Derive the route persona from the route simulation's personaId for convenience.
   * This is used to restore active persona on reloads before ancestor hooks run, and to
   * pass through to the view layer as well.
   */
  const routePersona = useMemo(() => {
    if (!routeSimulation) return null;
    return personas.find((persona) => persona.id === routeSimulation.personaId) ?? null;
  }, [personas, routeSimulation]);

  // ================================
  // Route-based state restoration
  // ================================
  // When route params point to a valid cert+sim but active state isn't set yet
  // (e.g. on a mobile reload), restore active state from the route to allow the
  // prep page to load properly.
  useEffect(() => {
    // Only attempt to restore state from the route when the URL points to a valid cert+sim pair.
    if (!routeCertification || !routeSimulation) return;

    // Restore active certification from the route if it doesn't match the current active state.
    if (activeCertification?.id !== routeCertification.id) {
      setActiveCertification(routeCertification);
    }

    // Restore active simulation from the route if it doesn't match the current active state.
    if (activeSimulation?.id !== routeSimulation.id) {
      setActiveSimulation(routeSimulation);
    }

    // Restore active persona from the route if it doesn't match the current active state.
    if (routePersona && activePersona?.id !== routePersona.id) {
      setActivePersona(routePersona);
    }
  }, [
    activeCertification?.id,
    activePersona?.id,
    activeSimulation?.id,
    routeCertification,
    routePersona,
    routeSimulation,
    setActiveCertification,
    setActivePersona,
    setActiveSimulation,
  ]);

  /**
   * View-level loading flag:
   * - True during initial app bootstrap (both loading flags set, no active state).
   * - Also true when data has finished loading but route-based state restoration
   *   is still pending (prevents a flash of empty content and the guard from
   *   firing prematurely on full-page reloads).
   */
  const isLoading = useMemo(() => {
    const inactiveCert = !activeCertification;
    const inactiveSim = !activeSimulation;
    const isInitializing = isLoadingInit && isLoadingStore && inactiveCert && inactiveSim;
    const isPendingRouteRestoreByMatch = isValidRoute && (inactiveCert || inactiveSim);
    const isPendingRouteRestoreByWarmup = shouldHoldForCatalogWarmup && !isRouteWarmupExpired;
    const isPendingRouteRestore = isPendingRouteRestoreByMatch || isPendingRouteRestoreByWarmup;

		// Hold the prep guard (i.e. return isLoading=true) when:
		// - Initializing (bootstrapping the app and loading data, with no active cert/sim yet)
		// - OR when route params point to a valid cert+sim but active state isn't set yet,
		// 	 indicating that we're waiting for `useActiveCertificationFromRoute` in the
		// 	 template to restore state from the URL. This can happen on slow mobile reloads
		// 	 where the store has data but ancestor hooks haven't run yet.
    return isInitializing || isPendingRouteRestore;
  }, [
    isLoadingInit,
    isLoadingStore,
    activeCertification,
    activeSimulation,
    isValidRoute,
    shouldHoldForCatalogWarmup,
    isRouteWarmupExpired,
  ]);

  // ================================
  // Guarded navigation
  // ================================
  // Once not loading, redirect to dashboard when required context is missing.
  // This prevents the prep view from rendering in an invalid state.
  useEffect(() => {
    if (!isLoading && (!activeCertification || !activeSimulation)) {
      navigateToDashboard();
    }
  }, [isLoading, activeCertification, activeSimulation, navigateToDashboard]);

  // ================================
  // Interaction handlers
  // ================================
  // Start-roleplay action:
  // - Convert IDs to strings for route helper compatibility.
  // - Navigate to role-play when both IDs are present.
  // - Fall back to dashboard when required IDs are unavailable.
  const handleStartRolePlay = useCallback(() => {
    const activeCertificationId = activeCertification?.id.toString();
    const activeSimulationId = activeSimulation?.id.toString();

    if (activeCertificationId && activeSimulationId) {
      navigateToRoleplay(activeCertificationId, activeSimulationId);
    } else {
      navigateToDashboard();
    }
  }, [activeCertification?.id, activeSimulation?.id, navigateToRoleplay, navigateToDashboard]);

  // ================================
  // Expose feature API
  // ================================
  // Expose raw store entities plus derived state/handlers for the view layer.
  return {
    activeCertification,
    activeSimulation,
    handleStartRolePlay,
    isLoading,
    personas,
  };
};
